/**
 * `DetailSurfaceShell` — single source of truth for the back-header
 * sibling + body wrapping element shared by every article-detail
 * surface (populated, skeleton, not-found). Composing through this
 * shell means a future chrome addition (sticky TOC, print, …) ripples
 * to all three states from one site rather than 8+ near-misses that
 * drift.
 *
 * Lives in its own module (rather than inside `articleDetailSurface.tsx`)
 * because the shell owns a different concern: the cross-state
 * composition contract (back-header sibling + body wrapping element).
 * The surrounding token constants and the `DetailSurfaceSkeleton` /
 * `DetailSurfaceNotFound` / `PolicyStatusCard` helpers are the
 * per-state details. Separating them here keeps the "shell wraps
 * three branches" story easy to read AND lets the shell be re-used
 * without dragging the per-state helpers along.
 *
 * Re-exported from `articleDetailSurface.tsx` so consumers see a
 * single entry-point surface — see that module's file-header for
 * the full module-organisation rationale.
 *
 * Prop shape — discriminated union over `as`
 * ==========================================
 *
 * `DetailSurfaceShellProps` is a discriminated union with three
 * variants — `'article'`, `'section'`, `'div'` — selected by the `as`
 * discriminator. Each variant declares ONLY the a11y attributes its
 * branch consumes:
 *
 *   - `'article'` — populated article surface; the implicit `article`
 *     landmark needs no explicit a11y attributes.
 *   - `'section'` — labelled-section landmark (not-found surface);
 *     `ariaLabelledBy` is required so AT can name the landmark.
 *   - `'div'` — skeleton live region; `role`, `ariaBusy`, `ariaLabel`,
 *     and `testId` are all required by the live-region contract.
 *
 * The discriminated union replaces the previous flat interface +
 * runtime guard combination. The type system now refuses any caller
 * who passes `as='div'` without `role='status'` (and the matching
 * `ariaBusy`/`ariaLabel`/`testId`), so no runtime assertion is needed
 * — the impossible state is unrepresentable at the call site.
 *
 * The wrapping element is dynamically resolved from the `as` prop. A
 * `switch` is used (rather than a single `<Wrapper>` JSX element) so
 * each branch's narrowed variant supplies only the attributes its
 * element type accepts — no synthetic cast, no spread of optionals.
 */

import type { JSX, ReactNode } from 'react';

import {
  DetailSurfaceBackHeader,
  type DetailSurfaceBackHeaderProps,
} from './detailSurfaceBackHeader.js';
import { pageClasses } from './detailSurfacePageClasses.js';

/**
 * Wrapping element used for the populated/skeleton/not-found body of an
 * article-detail surface. The three values cover every existing consumer:
 *
 *   - `'article'` for populated article surfaces (the article IS the
 *     page's primary content, so it carries the implicit `article`
 *     landmark).
 *   - `'section'` for not-found (the section is labelled by the heading
 *     id so AT can name the landmark; pair with `aria-labelledby`).
 *   - `'div'` for skeleton (carries `role="status"` and `aria-busy="true"`
 *     instead of a default landmark).
 */
export type DetailSurfaceWrapperElement = 'article' | 'section' | 'div';

/**
 * Populated article variant — the `<article>` landmark carries the
 * route's primary content. Needs no explicit a11y attributes.
 */
interface DetailSurfaceShellArticleProps {
  readonly as: 'article';
  readonly children: ReactNode;
}

/**
 * Not-found / labelled-section variant — the `<section>` is a navigable
 * landmark named by `aria-labelledby`. The labelled-section contract
 * requires the attribute, so it is REQUIRED on the variant.
 */
interface DetailSurfaceShellSectionProps {
  readonly as: 'section';
  /** `aria-labelledby` for the section landmark — required: a labelled
   * section is a navigable landmark. */
  readonly ariaLabelledBy: string;
  readonly children: ReactNode;
}

/**
 * Skeleton / live-region variant — the `<div>` carries `role="status"`
 * + `aria-busy="true"` + an accessible name. Every attribute is
 * required by the live-region contract; the type system enforces what
 * was previously a runtime assertion.
 */
interface DetailSurfaceShellSkeletonProps {
  readonly as: 'div';
  /** Required by the live-region contract — only `'status'` is
   * supported. Hardcoded as a literal so callers cannot pass an
   * unrelated role through this branch. */
  readonly role: 'status';
  /** Required — the skeleton is busy on render. */
  readonly ariaBusy: true;
  /** Required — the labelled live region must announce its purpose. */
  readonly ariaLabel: string;
  /** Required — skeleton consumers attach a testid for query-by-shape. */
  readonly testId: string;
  readonly children: ReactNode;
}

export type DetailSurfaceShellProps = (
  | DetailSurfaceShellArticleProps
  | DetailSurfaceShellSectionProps
  | DetailSurfaceShellSkeletonProps
) &
  DetailSurfaceBackHeaderProps;

export function DetailSurfaceShell(props: DetailSurfaceShellProps): JSX.Element {
  // The fragment carries the back-header sibling and
  // the wrapping body element. The back-header is rendered as a
  // sibling rather than a child of the body wrapper so the bar (a
  // fixed-positioned chrome element) does not live inside the page's
  // primary landmark.
  switch (props.as) {
    case 'article': {
      return (
        <>
          <DetailSurfaceBackHeader
            fallbackTo={props.fallbackTo}
            fixedTo={props.fixedTo}
            backLabel={props.backLabel}
          />
          <article data-feedback-clearance className={pageClasses}>
            {props.children}
          </article>
        </>
      );
    }
    case 'section': {
      return (
        <>
          <DetailSurfaceBackHeader
            fallbackTo={props.fallbackTo}
            fixedTo={props.fixedTo}
            backLabel={props.backLabel}
          />
          <section
            data-feedback-clearance
            className={pageClasses}
            aria-labelledby={props.ariaLabelledBy}
          >
            {props.children}
          </section>
        </>
      );
    }
    case 'div': {
      // Every required attribute (`role`, `aria-busy`, `aria-label`,
      // `data-testid`) is enforced by the variant's type. No runtime
      // guard is needed — TypeScript refuses any caller who omits
      // them on the `as="div"` branch.
      //
      // `role` is hardcoded to the literal `'status'` (rather than
      // forwarding `props.role`) because Biome's
      // `useAriaPropsSupportedByRole` rule resolves role attributes
      // statically; flowing the role through a prop would defeat the
      // check even though the variant's type already pins the value
      // to `'status'`. The type and the literal carry the same
      // structural guarantee.
      return (
        <>
          <DetailSurfaceBackHeader
            fallbackTo={props.fallbackTo}
            fixedTo={props.fixedTo}
            backLabel={props.backLabel}
          />
          <div
            role="status"
            data-feedback-clearance
            className={pageClasses}
            aria-busy={props.ariaBusy}
            aria-label={props.ariaLabel}
            data-testid={props.testId}
          >
            {props.children}
          </div>
        </>
      );
    }
  }
}
